import * as Sentry from '@sentry/vue';
import { VERSION_CONFIG } from '@/config/version';

/**
 * VersionService - Verifica la versión de la app usando version.json
 * NO extiende BaseService ya que no necesita autenticación ni Axios
 */
class VersionService {
  constructor() {
    this.versionJsonPath = VERSION_CONFIG.VERSION_JSON_PATH;
  }

  /**
   * Obtiene el commit actual de la app (build-time)
   * @returns {string}
   */
  getCurrentGitCommit() {
    return typeof __GIT_COMMIT__ !== 'undefined' ? __GIT_COMMIT__ : null;
  }

  /**
   * Obtiene la versión actual de la app (build-time)
   * @returns {string}
   */
  getCurrentVersion() {
    return typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : null;
  }

  /**
   * Fetch version.json con cache-busting
   * @returns {Promise<{version: string, gitCommit: string, buildTimestamp: string}|null>}
   */
  async fetchVersionJson() {
    try {
      const cacheBuster = Date.now();
      const url = `${this.versionJsonPath}?_=${cacheBuster}`;

      const response = await fetch(url, {
        method: 'GET',
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      Sentry.addBreadcrumb({
        message: 'Failed to fetch version.json',
        category: 'version',
        level: 'warning',
        data: {
          error: error.message,
          currentGitCommit: this.getCurrentGitCommit(),
          currentVersion: this.getCurrentVersion(),
        },
      });

      // En caso de error de red, retornamos null (asumimos versión válida)
      return null;
    }
  }

  /**
   * Verifica si la app está desactualizada comparando con version.json
   * @returns {Promise<{isStale: boolean, serverData: object|null}>}
   */
  async checkVersion() {
    const serverData = await this.fetchVersionJson();

    // Si no pudimos obtener version.json, asumimos que la versión es válida
    if (!serverData) {
      Sentry.addBreadcrumb({
        message: 'Version check skipped - could not fetch version.json',
        category: 'version',
        level: 'info',
      });

      return {
        isStale: false,
        serverData: null,
        reason: 'fetch_failed',
      };
    }

    const currentCommit = this.getCurrentGitCommit();
    const serverCommit = serverData.gitCommit;

    // Comparación primaria: git commit
    if (currentCommit && serverCommit) {
      const isStale = currentCommit !== serverCommit;

      Sentry.addBreadcrumb({
        message: `Version check: ${isStale ? 'STALE' : 'OK'}`,
        category: 'version',
        level: isStale ? 'warning' : 'info',
        data: {
          currentCommit,
          serverCommit,
          serverVersion: serverData.version,
          serverBuildTimestamp: serverData.buildTimestamp,
        },
      });

      return {
        isStale,
        serverData,
        reason: isStale ? 'commit_mismatch' : 'commit_match',
      };
    }

    // Fallback: comparar versión semver
    const currentVersion = this.getCurrentVersion();
    const serverVersion = serverData.version;

    if (currentVersion && serverVersion) {
      const isStale = this.compareVersions(currentVersion, serverVersion) < 0;

      Sentry.addBreadcrumb({
        message: `Version check (fallback): ${isStale ? 'STALE' : 'OK'}`,
        category: 'version',
        level: isStale ? 'warning' : 'info',
        data: {
          currentVersion,
          serverVersion,
        },
      });

      return {
        isStale,
        serverData,
        reason: isStale ? 'version_outdated' : 'version_ok',
      };
    }

    // Si no tenemos forma de comparar, asumimos OK
    return {
      isStale: false,
      serverData,
      reason: 'no_comparison_data',
    };
  }

  /**
   * Compara dos versiones semver
   * @param {string} version1
   * @param {string} version2
   * @returns {number} -1 si version1 < version2, 0 si iguales, 1 si version1 > version2
   */
  compareVersions(version1, version2) {
    const v1parts = version1.split('.').map((n) => parseInt(n, 10));
    const v2parts = version2.split('.').map((n) => parseInt(n, 10));

    for (let i = 0; i < Math.max(v1parts.length, v2parts.length); i++) {
      const v1part = v1parts[i] || 0;
      const v2part = v2parts[i] || 0;

      if (v1part < v2part) return -1;
      if (v1part > v2part) return 1;
    }

    return 0;
  }

  /**
   * Obtiene información del build actual
   * @returns {Object}
   */
  getCurrentVersionInfo() {
    return {
      version: this.getCurrentVersion(),
      gitCommit: this.getCurrentGitCommit(),
      buildTime: typeof __BUILD_TIMESTAMP__ !== 'undefined' ? __BUILD_TIMESTAMP__ : null,
      environment: import.meta.env.VITE_ENVIRONMENT || 'development',
      userAgent: navigator.userAgent,
      screenResolution: `${screen.width}x${screen.height}`,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
    };
  }
}

export default new VersionService();
